<template>
  <div class="pivot-sensor-chart mb-3">
    <div v-if="loading" class="col-xs-12 center-xs mt-2">
      <spinner /> Carregando histórico do pivô...
    </div>
    <div v-else-if="errorMsg" class="pivot-sensor-chart-empty">
      {{ errorMsg }}
    </div>
    <template v-else>
      <div v-if="sensorName" class="col-xs-12 end-xs btn-left mb-2">
        <b-btn-group>
          <b-btn v-for="p in periods" :key="p" size="sm" variant="primary" @click="fetchChart(p)"
            :disabled="p == period">{{ p }}</b-btn>
        </b-btn-group>
      </div>
      <pivot-trajectory-chart v-if="posSensorName" :pos-sensor-name="posSensorName" :pressure-sensor-name="sensorName"
        :center-lat="centerLat" :center-lon="centerLon" :is-mobile="isMobile" class="pivot-sensor-chart-item" />
      <div v-else-if="positionSeries" class="pivot-sensor-chart-item">
        <apexchart width="100%" height="280" :series="positionSeries" :options="positionOptions"></apexchart>
      </div>
      <div v-if="combinedSeries.length" class="pivot-sensor-chart-item">
        <apexchart width="100%" height="425" :series="combinedSeries" :options="combinedOptions"></apexchart>
      </div>
    </template>
    <div class="col-xs-12 mt-2 mr-2">
      <TabulatorEvents v-if="actuatorName" :fetch-page="fetchEventsPage" :equipment-name="actuatorName" :is-mobile="isMobile" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { monitChart, dirs, valves } from "@/api";
import { organizeLog } from "@/pages/components/monit/charts";
import TabulatorEvents from "@/pages/components/shared/TableEvents.vue";
import PivotTrajectoryChart from "./PivotTrajectoryChart.vue";

// O DIR do pivô também tem, no mesmo controlador físico, um "Sensor PIR" irmão
// (INTERFACE_OPTIONS.NAME = "PIR" em CAD_MODEL_SENSOR) que mede Posição/Pressão/
// Vazão em tempo real — é a fonte bruta que alimenta o "Posição:" mostrado no
// disco do atuador. O LOG_SENSOR desse sensor tem o histórico que o atuador
// sozinho não guarda (ActuatorCharts só grafica FLOW/VOL/DEPTH/OPEN do atuador).
// Posição fica em um gráfico à parte (escala 0-360°, sem relação com as outras);
// Atuação/Vazão/Pressão ficam combinadas num único gráfico pra comparar o
// comportamento do pivô (ligado/desligado, vazão, pressão) no mesmo eixo do tempo.
const POSITION_MEASURE = { type: "Posição", unit: "º", label: "POSIÇÃO", color: "#3d9161", yMax: 360, decimals: 0 };
const FLOW_MEASURE = { type: "Vazão", unit: "m³/h", label: "VAZÃO", color: "#eb753e", decimals: 2 };
const PRESSURE_MEASURE = { type: "Pressão", unit: "bar", label: "PRESSÃO", color: "#298abe", decimals: 2 };
const OPEN_COLOR = "#6c757d";

export default {
  name: "pivot-sensor-chart",
  components: {
    TabulatorEvents,
    PivotTrajectoryChart,
  },
  props: {
    dirAddr: [String, Number],
    actuatorName: String,
    isMobile: Boolean,
  },
  data() {
    return {
      loading: true,
      errorMsg: "",
      period: "1d",
      periods: ["1h", "1d", "3d", "7d"],
      controller: null,
      sensorName: null,
      posSensorName: null,
      centerLat: null,
      centerLon: null,
      sensorInfo: {},
      openData: [],
    };
  },
  computed: {
    positionSeries() {
      const info = this.sensorInfo[POSITION_MEASURE.type];
      if (!info || !info.data.length) return null;
      return [{ name: POSITION_MEASURE.label, unit: POSITION_MEASURE.unit, data: info.data }];
    },
    positionOptions() {
      return this.buildOptions(POSITION_MEASURE, `${POSITION_MEASURE.label} ao longo do tempo`);
    },
    combinedSeries() {
      const series = [];
      const flow = this.sensorInfo[FLOW_MEASURE.type];
      if (flow && flow.data.length) series.push({ name: FLOW_MEASURE.label, unit: FLOW_MEASURE.unit, data: flow.data });
      const pressure = this.sensorInfo[PRESSURE_MEASURE.type];
      if (pressure && pressure.data.length) series.push({ name: PRESSURE_MEASURE.label, unit: PRESSURE_MEASURE.unit, data: pressure.data });
      if (this.openData.length) series.push({ name: "ATUAÇÃO", unit: "", data: this.openData });
      return series;
    },
    combinedOptions() {
      const hasFlow = !!(this.sensorInfo[FLOW_MEASURE.type] && this.sensorInfo[FLOW_MEASURE.type].data.length);
      const hasPressure = !!(this.sensorInfo[PRESSURE_MEASURE.type] && this.sensorInfo[PRESSURE_MEASURE.type].data.length);
      const colors = [];
      const curves = [];
      const yaxis = [];
      if (hasFlow) {
        colors.push(FLOW_MEASURE.color);
        curves.push("smooth");
        yaxis.push(this.numericYAxis(FLOW_MEASURE.color, false));
      }
      if (hasPressure) {
        colors.push(PRESSURE_MEASURE.color);
        curves.push("smooth");
        yaxis.push(this.numericYAxis(PRESSURE_MEASURE.color, hasFlow));
      }
      if (this.openData.length) {
        colors.push(OPEN_COLOR);
        curves.push("stepline");
        // Escala binária (0/1) — eixo escondido pra não confundir com as outras
        // grandezas, a mesma abordagem usada em ActuatorCharts para ATUAÇÃO.
        yaxis.push({ min: 0, max: 1, show: false });
      }
      return {
        chart: {
          id: "pivot-sensor-chart-combined",
          height: 260,
          type: "line",
          zoom: { enabled: true, autoScaleYaxis: true },
          animations: { enabled: false },
          toolbar: {
            export: {
              csv: {
                headerCategory: "Data",
                filename: "Vazão, pressão e atuação do pivô",
                dateFormatter(timestamp) {
                  return moment(timestamp).format("DD/MM/YYYY HH:mm");
                },
              },
            },
            offsetY: -2,
          },
        },
        xaxis: {
          type: "datetime",
          labels: { datetimeUTC: false, format: "dd/MM/yyyy HH:mm" },
        },
        yaxis,
        stroke: { curve: curves, width: curves.map(() => 2) },
        title: {
          text: "Atuação, vazão e pressão ao longo do tempo",
          align: "left",
          offsetX: 10,
          offsetY: 15,
        },
        dataLabels: { enabled: false },
        tooltip: {
          x: { show: true, format: "dd/MM/yyyy HH:mm" },
        },
        legend: { show: true, position: "bottom", markers: { width: 10, height: 10 } },
        colors,
      };
    },
  },
  created() {
    this.controller = new AbortController();
    this.resolveSensor();
  },
  beforeDestroy() {
    this.controller.abort();
  },
  methods: {
    resolveSensor() {
      // Duas associações independentes alimentam esse componente: o "Sensor PIR"
      // do DIR (CAD_DIR.PIR, ângulo em graus — gráfico de linha simples, fallback)
      // e o "Sensor de posição" do atuador (CAD_VALVE.FK_SENSOR_POS, modelo SPG,
      // latitude/longitude — vira o mapa de trajetória + polar). Um cliente pode
      // ter só um dos dois, os dois, ou nenhum.
      Promise.all([dirs.list(), valves.list()])
        .then(([dirRet, valveRet]) => {
          // Ambos endpoints devolvem array puro de linhas (res.json(ret || [])),
          // não o envelope {status, value} usado no resto da API.
          const dirList = Array.isArray(dirRet) ? dirRet : [];
          const target = Number(this.dirAddr);
          const dirRow = dirList.find((d) => Number(d.ADDR) === target);
          if (dirRow) {
            this.centerLat = dirRow.LATITUDE != null ? Number(dirRow.LATITUDE) : null;
            this.centerLon = dirRow.LONGITUDE != null ? Number(dirRow.LONGITUDE) : null;
            if (dirRow.PIR && dirRow.EQUIP_ON_PIR) {
              this.sensorName = dirRow.EQUIP_ON_PIR;
            }
          }

          const valveList = Array.isArray(valveRet) ? valveRet : [];
          const valveRow = valveList.find((v) => v.NAME === this.actuatorName);
          if (valveRow && valveRow.FK_SENSOR_POS && valveRow.NAME_SENSOR_POS) {
            this.posSensorName = valveRow.NAME_SENSOR_POS;
          }

          if (!this.sensorName && !this.posSensorName) {
            throw new Error("Este DIR não possui um sensor de posição associado.");
          }

          if (this.sensorName) {
            this.fetchChart();
          } else {
            this.loading = false;
          }
        })
        .catch((err) => {
          this.loading = false;
          this.errorMsg =
            err.message || "Não foi possível carregar o histórico de posição.";
        });
    },
    periodStart(p) {
      const now = moment();
      switch (p) {
        case "7d":
          return moment().subtract(7, "days").hours(0).minutes(0);
        case "3d":
          return moment().subtract(3, "days").hours(0).minutes(0);
        case "1h":
          return now.subtract(1, "hours");
        case "1d":
        default:
          return moment().subtract(1, "days").hours(0).minutes(0);
      }
    },
    fetchEventsPage(page) {
      return monitChart
        .events({ NAME: this.actuatorName, PAGE: page }, this.controller)
        .then((ret) => ({
          rows: ret.status
            ? ret.value.map(({ TIME, NOTE }) => ({ NAME: TIME, VALUE: NOTE }))
            : [],
          total: ret.more ? ret.more.total : 0,
          totalPages: ret.more ? ret.more.totalPages : 0,
        }));
    },
    fetchChart(p) {
      if (p) this.period = p;
      this.loading = true;
      this.errorMsg = "";
      const end = moment();
      const start = this.periodStart(this.period);
      const measures = [POSITION_MEASURE, FLOW_MEASURE, PRESSURE_MEASURE];
      const data = {
        EQP: "SENSOR",
        NAME: this.sensorName,
        COLUMN: "UNIT",
        CONDITION: {
          TYPES: measures.map((m) => m.type),
          UNITS: measures.map((m) => m.unit),
        },
        START: start.format("YYYY-MM-DD HH:mm"),
        END: end.format("YYYY-MM-DD HH:mm"),
      };
      Promise.all([
        monitChart
          .create(data, this.controller)
          .then((ret) => {
            if (!ret.status) throw new Error(ret.log || "Falha ao buscar histórico.");
            return organizeLog(
              ret,
              "SENSOR",
              this.sensorName,
              measures.map((m) => ({ TYPE: m.type, UNIT: m.unit, PER_READING: true })),
              "chartsPage"
            );
          })
          .then((result) => (result.status ? result.value : {}))
          .catch((err) => {
            console.error(err);
            return {};
          }),
        this.fetchOpenData(start, end),
      ]).then(([sensorInfo, openData]) => {
        this.sensorInfo = sensorInfo;
        this.openData = openData;
        if (!this.positionSeries && !this.combinedSeries.length) {
          this.errorMsg = "Não há leituras do pivô no período.";
        }
        // Mesmo delay usado em ActuatorCharts: montar o apexchart no mesmo frame
        // em que a aba "Gráficos" troca de v-if pra v-else faz o ApexCharts medir
        // a largura do container antes do layout da troca de aba assentar, e o
        // gráfico nasce pequeno até a próxima atualização de série recalcular.
        setTimeout(() => {
          this.loading = false;
        }, 150);
      });
    },
    // A Atuação vem do histórico do atuador (LOG_ACTUATOR), não do sensor de
    // posição — busca à parte, e degrada silenciosamente (só omite a série) se
    // falhar, pra não derrubar os gráficos de vazão/pressão que já carregaram.
    fetchOpenData(start, end) {
      if (!this.actuatorName) return Promise.resolve([]);
      const data = {
        EQP: "ACTUATOR",
        NAME: this.actuatorName,
        START: start.format("YYYY-MM-DD HH:mm"),
        END: end.format("YYYY-MM-DD HH:mm"),
      };
      return monitChart
        .create(data, this.controller)
        .then((ret) => {
          if (!ret.status) throw new Error(ret.log || "Falha ao buscar histórico de atuação.");
          return organizeLog(ret, "ACTUATOR", this.actuatorName, [], null, 0);
        })
        .then((result) => (result.status && result.value.OPEN ? result.value.OPEN.data : []))
        .catch((err) => {
          console.error(err);
          return [];
        });
    },
    numericYAxis(color, opposite) {
      return {
        min: 0,
        opposite,
        axisTicks: { show: false },
        axisBorder: { show: false, color },
        labels: {
          offsetX: opposite ? 6 : 0,
          style: { fontSize: "10px", colors: color },
          formatter: (v) => `${Number(v).toFixed(2)}`,
        },
        title: { show: false },
      };
    },
    buildOptions(measure, title) {
      return {
        chart: {
          id: `pivot-sensor-chart-${measure.type}`,
          height: 200,
          type: "line",
          zoom: { enabled: true, autoScaleYaxis: true },
          animations: { enabled: false },
          toolbar: {
            export: {
              csv: {
                headerCategory: "Data",
                filename: `${measure.label} do pivô`,
                dateFormatter(timestamp) {
                  return moment(timestamp).format("DD/MM/YYYY HH:mm");
                },
              },
            },
            offsetY: -2,
          },
        },
        xaxis: {
          type: "datetime",
          labels: { datetimeUTC: false, format: "dd/MM/yyyy HH:mm" },
        },
        yaxis: {
          min: 0,
          ...(measure.yMax ? { max: measure.yMax, tickAmount: 4 } : {}),
          labels: { formatter: (v) => `${v.toFixed(measure.decimals)} ${measure.unit}` },
        },
        stroke: { curve: "straight", width: 2 },
        title: {
          text: title,
          align: "left",
          offsetX: 10,
          offsetY: 15,
        },
        dataLabels: { enabled: false },
        tooltip: {
          x: { show: true, format: "dd/MM/yyyy HH:mm" },
          y: { formatter: (v) => `${Number(v).toFixed(measure.decimals)} ${measure.unit}` },
        },
        legend: { show: false },
        colors: [measure.color],
      };
    },
  },
};
</script>

<style scoped>
.pivot-sensor-chart {
  min-height: 60px;
}
.pivot-sensor-chart-empty {
  font-size: 0.85em;
  color: #888;
  padding: 8px 0;
}
.pivot-sensor-chart-item {
  margin-bottom: 12px;
}
</style>
